import { useEffect, useState } from 'react'
import { fallbackSalutation, isoWeekday, salutationQuery } from '@netwatchai/chat-core'

interface UseSalutationOptions {
  apiEndpoint: string
  headers?: Record<string, string>
  /** Reader's display name; '' greets anonymously ("Good morning"). */
  name: string
  enabled: boolean
  /** Bumped on every "New chat" so each empty screen draws a fresh variant
   *  from the server's pool instead of pinning the first one for the session. */
  seq: number
}

/**
 * The new-chat greeting for the widget's chat landing — the same `/salutation`
 * the dashboard reads, reached with the widget's header auth (the endpoint
 * takes `NetwatchClientDep`, the same lightweight header check `/confidence`
 * uses).
 *
 * No react-query here (the widget doesn't ship it): one GET per empty screen,
 * and the response is a server-side cache read, so re-asking is cheap.
 *
 * The deterministic ladder renders first so the heading is never blank and
 * stands in permanently if the request fails — a greeting must not be able to
 * take down the screen it decorates. `fallbackSalutation` mirrors the server's
 * own `_fallback`, so the line doesn't visibly rewrite itself on arrival.
 */
export function useSalutation({ apiEndpoint, headers, name, enabled, seq }: UseSalutationOptions) {
  // Frozen per draw rather than ticked: a heading that silently rewrites
  // itself mid-read is worse than one that is an hour stale.
  const [clock] = useState(() => new Date())
  const [text, setText] = useState(() => fallbackSalutation(clock, name))

  // `headers` is the caller's memoized auth pair, so it is safe as a dep —
  // no ref dance needed to keep the effect from re-firing every render.
  useEffect(() => {
    if (!enabled) return
    // The previous phrase stays on screen while the next draw is in flight —
    // without it every new chat would flash the fallback before settling.
    let live = true
    const qs = salutationQuery(isoWeekday(clock), clock.getHours(), name)
    fetch(`${apiEndpoint}/salutation?${qs}`, { headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (live && body?.text) setText(String(body.text))
      })
      .catch(() => {
        /* keep the deterministic line */
      })
    return () => {
      live = false
    }
  }, [apiEndpoint, headers, clock, name, enabled, seq])

  return text
}
